import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  Paper,
  Popper,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { muiTheme } from '../theme/muiTheme';
import { franchiseApi } from '../services/franchiseApi';
import { emitNotificationsChanged, NOTIFICATIONS_CHANGED_EVENT } from '../services/notificationEvents';
import { useOpenNotification } from '../hooks/useOpenNotification';
import NotificationListItem from './NotificationListItem';
import type { AppNotification, NotificationSummary } from '../types/notifications';

// How many of the newest notifications the dropdown shows; the rest are a "View all" away.
export const NOTIFICATION_PREVIEW_COUNT = 3;

const DROPDOWN_ID = 'notification-bell-dropdown';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: NotificationSummary }
  | { kind: 'error' };

type Preview =
  | { kind: 'loading' }
  | { kind: 'ready'; items: AppNotification[] }
  | { kind: 'error' };

/**
 * The top bar's bell, badged with the unread count, opening a dropdown of the newest notifications —
 * read and unread told apart — with "Mark all as read" and a link to the whole inbox.
 *
 * Renders nothing until the user has had at least one notification: with no callers sending any yet,
 * an always-present bell would be an icon that never does anything. It is also absent while loading and
 * after a failed read, rather than showing a count that may be wrong.
 *
 * The count is read once, when the app loads, and again only when this app has changed it (a
 * notification read, or a push arriving in the foreground). There is no polling: a notification that
 * arrives while the page is open shows on the next load. The dropdown reads its list each time it opens.
 */
const NotificationBell: React.FC = () => {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [preview, setPreview] = useState<Preview>({ kind: 'loading' });
  const [markingAll, setMarkingAll] = useState(false);
  const openNotification = useOpenNotification();
  // Bumped on each open, so a list requested for an earlier opening can't land in this one.
  const openings = useRef(0);

  // Escape closes the dropdown, as it would a menu.
  useEffect(() => {
    if (!anchor) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAnchor(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [anchor]);

  useEffect(() => {
    let cancelled = false;
    // Responses can land out of order (the load and an event-driven refetch in flight together); only
    // the latest request may set the state.
    let latest = 0;

    const refresh = async () => {
      const seq = ++latest;
      try {
        const summary = await franchiseApi.getNotificationSummary();
        if (!cancelled && seq === latest) setState({ kind: 'ready', summary });
      } catch {
        if (!cancelled && seq === latest) setState({ kind: 'error' });
      }
    };

    void refresh();
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);

    return () => {
      cancelled = true;
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
    };
  }, []);

  if (state.kind !== 'ready' || state.summary.total === 0) return null;

  const { unread } = state.summary;
  const Icon = unread > 0 ? NotificationsIcon : NotificationsNoneIcon;
  const close = () => setAnchor(null);

  const toggleDropdown = async (target: HTMLElement) => {
    if (anchor) {
      close();
      return;
    }
    setAnchor(target);
    const opening = ++openings.current;
    setPreview({ kind: 'loading' });
    try {
      const page = await franchiseApi.listNotifications({ limit: NOTIFICATION_PREVIEW_COUNT });
      if (opening === openings.current) setPreview({ kind: 'ready', items: page.data });
    } catch (err) {
      console.error('Failed to load notifications:', err);
      if (opening === openings.current) setPreview({ kind: 'error' });
    }
  };

  // Local state first, so the badge drops the moment the click lands; the changed event the open
  // emits once the server agrees then re-reads the real count.
  const markReadLocally = (ids: string[] | 'all') => {
    const now = new Date().toISOString();
    setPreview(prev =>
      prev.kind === 'ready'
        ? { ...prev, items: prev.items.map(n => (ids === 'all' || ids.includes(n.id)) && !n.read_at ? { ...n, read_at: now } : n) }
        : prev
    );
    setState(prev =>
      prev.kind === 'ready'
        ? {
            ...prev,
            summary: { ...prev.summary, unread: ids === 'all' ? 0 : Math.max(0, prev.summary.unread - ids.length) },
          }
        : prev
    );
  };

  const open = (notification: AppNotification) => {
    if (!notification.read_at) markReadLocally([notification.id]);
    close();
    openNotification(notification);
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await franchiseApi.markAllNotificationsRead();
      markReadLocally('all');
      emitNotificationsChanged();
    } catch (err) {
      console.error('Failed to mark notifications read:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={event => void toggleDropdown(event.currentTarget)}
        size="small"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={anchor ? 'true' : undefined}
        aria-controls={anchor ? DROPDOWN_ID : undefined}
        title="Notifications"
        sx={{ color: '#20cbd4' }}
      >
        {/* No Badge at zero: an invisible Badge still renders its content for screen readers. */}
        {unread > 0 ? (
          <Badge badgeContent={unread} max={99} color="error">
            <Icon fontSize="small" />
          </Badge>
        ) : (
          <Icon fontSize="small" />
        )}
      </IconButton>

      {/* A Popper rather than a Popover: a Popover is a modal, and its scroll lock hides the page's
          scrollbar and pads <body> to make up the width, which the fixed top bar doesn't get, so the
          bar jumps sideways. Fixed positioning keeps the dropdown attached to that fixed bar. Above the
          top bar's z-index (App.css .top-bar). */}
      <ThemeProvider theme={muiTheme}>
        <Popper
          open={Boolean(anchor)}
          anchorEl={anchor}
          placement="bottom-end"
          popperOptions={{ strategy: 'fixed' }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          sx={{ zIndex: 1300 }}
        >
          <ClickAwayListener
            onClickAway={event => {
              // The bell's own click toggles the dropdown; don't let it also count as a click away.
              if (anchor?.contains(event.target as Node)) return;
              close();
            }}
          >
            <Paper
              id={DROPDOWN_ID}
              role="dialog"
              aria-label="Notifications"
              elevation={8}
              sx={{ width: 360, maxWidth: 'calc(100vw - 32px)' }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', px: 2, py: 1.5, gap: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Notifications</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {unread === 0 ? 'No unread notifications' : `${unread} unread`}
                  </Typography>
                </Box>
                {unread > 0 && (
                  <Tooltip title="Mark all as read">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Mark all as read"
                        onClick={() => void markAllRead()}
                        disabled={markingAll}
                      >
                        <DoneAllIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>

              <Divider />

              {preview.kind === 'loading' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} aria-label="Loading notifications" />
                </Box>
              )}
              {preview.kind === 'error' && (
                <Alert severity="error" sx={{ m: 2 }}>Your notifications could not be loaded.</Alert>
              )}
              {preview.kind === 'ready' && preview.items.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  You have no notifications.
                </Typography>
              )}
              {preview.kind === 'ready' && preview.items.length > 0 && (
                <List disablePadding>
                  {preview.items.map((notification, i) => (
                    <NotificationListItem
                      key={notification.id}
                      notification={notification}
                      onOpen={open}
                      divider={i < preview.items.length - 1}
                    />
                  ))}
                </List>
              )}

              <Divider />

              <Box sx={{ p: 1 }}>
                <Button component={Link} to="/notifications" onClick={close} fullWidth size="small">
                  View all
                </Button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </ThemeProvider>
    </>
  );
};

export default NotificationBell;
